import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Icon, notification } from 'antd';
import moment from 'moment';
import mixpanel from 'mixpanel-browser';
import sizeMe from 'react-sizeme';
import firebase from 'firebase';
import { Map } from 'immutable';
import { push } from 'connected-react-router';
import Promise from 'bluebird';
import chartColors from 'utils/chartcolors';
import cognitesdk from '@cognite/cognitesdk';
import RightSidebar from 'components/RightSidebar';
import { TagSelectContainer } from 'containers';
import EditTagSidebar from 'components/ChartView/EditTagSidebar';
import InfographicStyle from 'components/InfographicStyle';
import EditImageSidebar from 'components/EditImageSidebar';
import Infographic from 'components/Infographic';
import TitleView from 'components/TitleView';

const DEFAULT_INFOGRAPHIC_TITLE = 'CLICK TO EDIT TITLE';

const Wrapper = sizeMe({
  monitorHeight: true,
})(Infographic);

const infoLoader = tags => async values => {
  const result = await Promise.map(tags, async t => {
    let tempres = null;
    try {
      tempres = await cognitesdk.getLatestDataPoint({
        tagId: t,
      });
    } catch (ex) {
      if (ex.response && ex.response.status === 404) {
        tempres = {
          items: [
            {
              value: undefined,
            },
          ],
        };
      }
    }
    return {
      id: t,
      value: tempres.items[0].value,
    };
  });
  const output = {};
  result.forEach(r => {
    output[r.id] = r.value;
  });
  return output;
};

const Container = styled.div`
  margin: -16px;
  display: flex;
  flex-direction: column;
  background: white;
`;

const Topbar = styled.div`
  background: rgb(67, 75, 87);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionWrapper = styled.div`
  float: right;
  margin-right: 15px;
  display: flex;
  align-items: center;
`;

const ActionItemWrapper = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  margin-left: 5px;
  margin-right: 5px;
  padding: 5px;
  font-size: 0.8rem;
  white-space: nowrap;
  background: ${props => (props.active ? '#00253a' : 'inherit')};

  i {
    font-size: 1.5rem;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`;

class InfographicViewContainer extends React.Component {
  constructor(props) {
    super(props);
    const { match: { params: { id } } } = props;
    if (id !== 'new') {
      mixpanel.track('Viewed infographic', {
        id,
      });
    }
    this.state = {
      id: id === 'new' ? null : id,
      title: DEFAULT_INFOGRAPHIC_TITLE,
      editable: true,
      sidebarToggle: null,
      boxes: Map(),
      loading: true,
      imageUrl:
        'https://firebasestorage.googleapis.com/v0/b/operational-intelligence.appspot.com/o/infographic-images%2F01902b8b-1c9a-442c-98e6-a42ba51d9e51?alt=media&token=f2a3e8e7-22d6-45b9-bdc8-e25f921556f6',
      loader: () => ({}),
      hiddenSeries: {},
    };
  }

  async componentDidMount() {
    if (this.state.id) {
      firebase
        .database()
        .ref('infographics')
        .child(this.state.id)
        .once('value', snap => {
          const infographic = snap.val();
          const rawBoxes = infographic.boxes;
          let boxes = Map();
          rawBoxes.forEach(r => {
            boxes = boxes.set(r.id, r);
          });
          this.setState({
            boxes,
            loader: infoLoader(boxes.toArray().map(b => b.id)),
            title: infographic.title,
            editable: infographic.creator.uid === this.props.uid,
            imageUrl: infographic.imageUrl,
            loading: false,
            hiddenSeries: infographic.hiddenSeries || {},
          });
        });
    } else {
      this.setState({ editable: true, loading: false });
    }
  }

  onChangeTitle = e => {
    this.setState({
      title: e.target.value.toUpperCase(),
    });
  };

  onChangeImage = url => {
    this.setState({
      imageUrl: url,
      sidebarToggle: null,
    });
  };

  onSelectTag = rawTag => {
    let { boxes } = this.state;
    const tag = {
      id: rawTag.value,
      unit: rawTag.engunit,
      description: rawTag.description,
      value: 0,
      valueInRange: true,
      color: chartColors[boxes.size],
      typicalValue: rawTag.typicalValue,
      zero: rawTag.zero,
      span: rawTag.span,
    };
    boxes = boxes.set(tag.id, {
      left: 0,
      top: 0,
      ...tag,
    });
    const loader = infoLoader(boxes.toArray().map(b => b.id));
    this.setState({
      boxes,
      loader,
    });
  };

  onSave = async e => {
    e.preventDefault();

    if (this.state.boxes.size === 0) {
      notification.error({
        message: 'Error',
        description: 'You need to add some tags to save the infographic.',
      });
      return;
    }
    if (
      this.state.title === DEFAULT_INFOGRAPHIC_TITLE ||
      this.state.title.length === 0
    ) {
      notification.error({
        message: 'Error',
        description: 'Edit the title of the infographic before saving.',
      });
      return;
    }

    const infographic = {
      title: this.state.title.trim(),
      imageUrl: this.state.imageUrl,
      creator: {
        uid: this.props.uid,
        email: this.props.email,
      },
      updatedAt: firebase.database.ServerValue.TIMESTAMP,
      boxes: this.state.boxes.toArray().map(box => {
        const boxReplica = {};
        Object.keys(box).forEach(key => {
          if (box[key]) {
            boxReplica[key] = box[key];
          }
        });
        return boxReplica;
      }),
      hiddenSeries: this.state.hiddenSeries,
    };

    if (this.state.id) {
      // The infographic already exists
      await firebase
        .database()
        .ref('infographics')
        .child(this.state.id)
        .set(infographic);
      if (this.state.sidebarToggle) {
        this.setState({
          sidebarToggle: null,
        });
      }
    } else {
      // This is a new infographic
      infographic.createdAt = infographic.updatedAt;
      const newRef = firebase
        .database()
        .ref('infographics')
        .push();
      const updates = {
        [`users/${this.props.uid}/infographics/${newRef.key}`]: true,
        [`infographics/${newRef.key}`]: infographic,
      };
      await firebase
        .database()
        .ref()
        .update(updates);
      this.setState({
        id: newRef.key,
      });
      this.props.dispatch(push(`/infographics/${newRef.key}`));
    }
    notification.success({
      message: 'Saved',
      description: 'Your infographic has been saved.',
    });
  };

  toggleHideTag = tag => {
    const { hiddenSeries } = this.state;
    this.setState({
      hiddenSeries: {
        ...hiddenSeries,
        [tag]: !hiddenSeries[tag],
      },
    });
  };

  convertToChart = async () => {
    const { boxes, title } = this.state;
    const { email, uid } = this.props;
    const tags = boxes.toArray().map(b => ({
      value: b.id,
      label: b.description || '',
      span: b.span || 0,
      typicalValue: b.typicalValue || 0,
      zero: b.zero || 0,
      engunit: b.unit || '',
    }));
    const domain = [+moment().subtract(1, 'year'), +moment()];
    const colors = boxes.toArray().map(b => b.color);
    const newRef = firebase
      .database()
      .ref('charts')
      .push();
    const chart = {
      title,
      domain,
      subDomain: domain,
      tags,
      colors,
      isPublic: false,
      creator: { uid, email },
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      updatedAt: firebase.database.ServerValue.TIMESTAMP,
    };
    const updates = {
      [`users/${uid}/charts/${newRef.key}`]: true,
      [`charts/${newRef.key}`]: chart,
    };
    await firebase
      .database()
      .ref()
      .update(updates);
    return this.props.dispatch(push(`/charts/${newRef.key}`));
  };

  handleColorChange = (color, id) => {
    const boxes = this.state.boxes.update(id, b => ({
      ...b,
      color: color.hex,
    }));
    this.setState({
      boxes,
    });
  };

  updateBoxPosition = (id, { left, top }) => {
    const { boxes } = this.state;
    this.setState({
      boxes: boxes.update(id, t => ({
        ...t,
        left,
        top,
      })),
    });
  };

  removeTag = id => {
    const boxes = this.state.boxes.remove(id);
    const loader = infoLoader(boxes.toArray().map(b => b.id));
    this.setState({
      boxes,
      loader,
    });
  };

  render() {
    const {
      editable,
      title,
      sidebarToggle,
      boxes,
      loader,
      imageUrl,
      loading,
      hiddenSeries,
    } = this.state;
    if (loading) {
      return <div>Loading...</div>;
    }
    return (
      <Container>
        <Topbar>
          <TitleView
            title={title}
            onChange={this.onChangeTitle}
            defaultTitle={DEFAULT_INFOGRAPHIC_TITLE}
            disabled={!editable}
          />
          <ActionWrapper>
            {editable && (
              <ActionItemWrapper
                active={sidebarToggle === 'add-tag'}
                onClick={() =>
                  this.setState({
                    sidebarToggle:
                      sidebarToggle === 'add-tag' ? null : 'add-tag',
                  })
                }
              >
                <Icon type="tags" />
                <div>Add tag</div>
              </ActionItemWrapper>
            )}
            {editable && (
              <ActionItemWrapper
                active={sidebarToggle === 'edit-tag'}
                onClick={() =>
                  this.setState({
                    sidebarToggle:
                      sidebarToggle === 'edit-tag' ? null : 'edit-tag',
                  })
                }
              >
                <Icon type="tags-o" />
                <div>Edit tag</div>
              </ActionItemWrapper>
            )}
            {editable && (
              <ActionItemWrapper
                active={sidebarToggle === 'image'}
                onClick={() =>
                  this.setState({
                    sidebarToggle: sidebarToggle === 'image' ? null : 'image',
                  })
                }
              >
                <Icon type="picture" />
                <div>Image</div>
              </ActionItemWrapper>
            )}
            {editable && (
              <ActionItemWrapper onClick={this.onSave}>
                <Icon type="save" />
                <div>Save</div>
              </ActionItemWrapper>
            )}
            <ActionItemWrapper onClick={this.convertToChart}>
              <Icon type="line-chart" />
              <div>Chart</div>
            </ActionItemWrapper>
          </ActionWrapper>
        </Topbar>
        <ContentContainer>
          <RightSidebar open={sidebarToggle !== null} width={650}>
            {sidebarToggle === 'edit-tag' && (
              <EditTagSidebar
                tags={boxes.toArray().map(b => ({
                  ...b,
                  value: b.id,
                  label: b.description,
                  engunit: b.unit,
                }))}
                colors={boxes.map(b => b.color).toJS()}
                handleColorChange={this.handleColorChange}
                removeTag={this.removeTag}
                hiddenSeries={hiddenSeries}
                toggleHide={this.toggleHideTag}
              />
            )}
            {sidebarToggle === 'add-tag' && (
              <div style={{ padding: '10px' }}>
                <TagSelectContainer
                  onSelect={this.onSelectTag}
                  currentTags={boxes.toArray().map(b => ({
                    ...b,
                    value: b.id,
                  }))}
                  removeTag={this.removeTag}
                  colors={boxes.map(b => b.color).toJS()}
                />
              </div>
            )}
            {sidebarToggle === 'image' && (
              <EditImageSidebar onChangeImage={this.onChangeImage} />
            )}
          </RightSidebar>
          <Wrapper
            image={imageUrl}
            loader={loader}
            boxes={boxes.toArray().filter(b => !hiddenSeries[b.id])}
            updatePosition={this.updateBoxPosition}
            styledWrapper={InfographicStyle}
            pollInterval={2000}
          />
        </ContentContainer>
      </Container>
    );
  }
}

const mapStateToProps = state => ({
  uid: state.auth.uid,
  email: state.auth.email,
});

export default connect(mapStateToProps)(InfographicViewContainer);



// WEBPACK FOOTER //
// ./src/containers/InfographicViewContainer.js